import { useContext } from 'react';
import styled from '@emotion/styled';
import { TAudienceCombinationType } from '@definitions/states';
import { useAppTranslation } from '@hooks/useAppTranslation';
import {
  ProjectAudienceReviewerGroupSegmentType,
  ProjectAudienceReviewerGroupType,
} from '@pages/types';
import { Avatar, Button, colors, IconButton, Space, Text } from 'common-components';

import SegmentsTreeLeaf from './SegmentsTreeLeaf';
import TreeWrapper from './TreeWrapper';
import AddButtonSegment from './AddButtonSegment';

import { AudienceContext } from '@/context';

export type TAudienceCollapseType = 'contributors' | 'reviewers';

type SegmentsTreePropsType = {
  type: TAudienceCollapseType
  audience: ProjectAudienceReviewerGroupType[]
  combination: TAudienceCombinationType
  modifEnabled?: boolean
};

const Wrapper = styled.div`
  width: 100%;
  padding: 8px;
  border-radius: 12px;
  border: 1px solid ${colors.gray200};
  max-width: 918px;
`;

const SegmentsTree = ({ type, audience, combination, modifEnabled }: SegmentsTreePropsType) => {
  const { translate } = useAppTranslation();

  const {
    addGroup,
    deleteGroup,
    addSegment,
    deleteSegment,
    changeTreeCombination,
    changeGroupCombination,
  } = useContext(AudienceContext);

  const handleAddSegment = (segment: ProjectAudienceReviewerGroupSegmentType, groupIdx: number) =>
    addSegment(type, groupIdx, segment);

  const handleAddGroup = () => addGroup(type);

  const handleDeleteGroup = (groupIdx: number) => deleteGroup(type, groupIdx);

  const handleDeleteSegment = (
    deletedSegment: ProjectAudienceReviewerGroupSegmentType,
    groupIdx: number,
  ) => deleteSegment(type, groupIdx, deletedSegment);

  const handleTreeCombinationChange = (value: TAudienceCombinationType) =>
    changeTreeCombination(type, value);

  const handleGroupCombinationChange = (value: TAudienceCombinationType, groupIdx: number) =>
    changeGroupCombination(type, groupIdx, value);

  return (
    <Wrapper>
      <TreeWrapper
        defaultCombination={combination}
        contentLength={audience?.length}
        onSelectChange={handleTreeCombinationChange}
        disabled={modifEnabled === false}
      >
        {audience?.map((segmentsGroup, idx) => (
          <SegmentsTreeLeaf
            key={idx}
            first={idx === 0}
            last={idx + 1 === audience.length}
            single={audience.length === 1}
          >
            {modifEnabled && (
              <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
                <IconButton
                  icon="Trash01"
                  type="link"
                  size={'xs'}
                  iconProps={{ color: colors.gray400 }}
                  onClick={() => handleDeleteGroup(idx)}
                />
              </Space>
            )}
            <TreeWrapper
              defaultCombination={segmentsGroup.combination}
              contentLength={segmentsGroup.segments.length}
              onSelectChange={handleGroupCombinationChange}
              disabled={modifEnabled === false}
              groupIdx={idx}
            >
              <Space orientation="vertical" style={{ width: '100%', paddingRight: 24 }}>
                {segmentsGroup.segments.map((segment) => (
                  <SegmentsTreeLeaf
                    key={segment.idSegment}
                    single={segmentsGroup.segments.length === 1}
                    segment
                  >
                    <Space
                      style={{
                        width: '100%',
                        justifyContent: 'space-between',
                      }}
                    >
                      <Space size={4}>
                        <Avatar size="sm" />
                        <Text size="sm" color={colors.gray600}>
                          {segment.name}
                        </Text>
                      </Space>
                      {modifEnabled && (
                        <IconButton
                          icon="Trash01"
                          type="link"
                          size="xs"
                          iconProps={{ color: colors.gray400 }}
                          onClick={() => handleDeleteSegment(segment, idx)}
                        />
                      )}
                    </Space>
                  </SegmentsTreeLeaf>
                ))}
              </Space>
            </TreeWrapper>
            {modifEnabled && <AddButtonSegment groupIdx={idx} onSegmentClick={handleAddSegment} />}
          </SegmentsTreeLeaf>
        ))}
      </TreeWrapper>
      {modifEnabled && (
        <Button size="sm" iconLeft="FolderPlus" style={{ marginTop: 8 }} onClick={handleAddGroup}>
          {translate('project.audience.buttons.addGroup')}
        </Button>
      )}
    </Wrapper>
  );
};

export default SegmentsTree;
